import {createApi} from '@reduxjs/toolkit/query/react';
import {AxiosRequestHeaders} from 'axios';

import httpBaseQuery from '../../../shared/api/rtk-query/httpBaseQuery';
import {ApiUrls} from '../../../shared/config/model/urls';
import IServerAlertRule from '../model/ServerAlertRule/IServerAlertRule';
import CreateServerAlertRuleType from '../model/ServerAlertRule/CreateServerAlertRuleType';
import UpdateServerAlertRuleType from '../model/ServerAlertRule/UpdateServerAlertRuleType';
import IServerAlertRuleRecommendations from '../model/ServerAlertRule/IServerAlertRuleRecommendations';
import IHydraResponse from '../../../shared/api/model/HydraResponse';
import IPagination from '../../../shared/api/model/IPagination';
import {paginationAdapter} from '../../../shared/api/paginationHelper';
import {providesListUuid} from '../../../shared/api/rtk-query/RTKQueryHelper';
import {buildQuery} from '../../../shared/api/query';
import ICollectionParams from '../../../shared/api/model/ICollectionParams';

export const serverAlertRuleAPI = createApi({
    reducerPath: 'serverAlertRuleAPI',
    baseQuery: httpBaseQuery(),
    tagTypes: ['ServerAlertRule'],
    refetchOnMountOrArgChange: 60,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        addServerAlertRule: build.mutation<IServerAlertRule, CreateServerAlertRuleType>({
            query: (data) => ({
                path: ApiUrls.SERVER_ALERT_RULES(),
                method: 'POST',
                data,
            }),
            invalidatesTags: [{ type: 'ServerAlertRule', id: 'LIST' }],
        }),
        getServerAlertRules: build.query<IPagination<IServerAlertRule>, ICollectionParams>({
            query: ({page, sort, filters, limit}) => ({
                path: buildQuery(ApiUrls.SERVER_ALERT_RULES(page, sort, filters, limit)),
                headers: {Accept: 'application/ld+json'} as unknown as AxiosRequestHeaders,
            }),
            providesTags: (result) => providesListUuid(result?.items, 'ServerAlertRule'),
            transformResponse(
                rawResult: IHydraResponse<IServerAlertRule>,
            ): Promise<IPagination<IServerAlertRule>> | IPagination<IServerAlertRule> {
                return paginationAdapter(rawResult);
            },
        }),
        getServerAlertRule: build.query<IServerAlertRule, string>({
            query: (uuid) => ({
                path: ApiUrls.SERVER_ALERT_RULE(uuid),
            }),
            providesTags: (result, error, uuid) => [{ type: 'ServerAlertRule', id: uuid }],
        }),
        updateServerAlertRule: build.mutation<IServerAlertRule, UpdateServerAlertRuleType>({
            query: ({uuid, ...data}) => ({
                path: ApiUrls.SERVER_ALERT_RULE(uuid),
                method: 'PUT',
                data,
            }),
            invalidatesTags: (result, error, {uuid}) => [
                { type: 'ServerAlertRule', id: uuid },
                { type: 'ServerAlertRule', id: 'LIST' },
            ],
        }),
        deleteServerAlertRule: build.mutation<void, string>({
            query: (uuid) => ({
                path: ApiUrls.SERVER_ALERT_RULE(uuid),
                method: 'DELETE',
            }),
            invalidatesTags: () => [{ type: 'ServerAlertRule', id: 'LIST' }],
        }),
        getServerAlertRuleRecommendations: build.query<IServerAlertRuleRecommendations, void>({
            query: () => ({
                path: ApiUrls.SERVER_ALERT_RULE_RECOMMENDATIONS,
            }),
        }),
    }),
});
